import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { useMarkNotificationReadForEntity } from "../../context/NotificationContext";
import { Header } from "../../components/vc/Header";
import { PrimaryButton, SecondaryButton } from "../../components/vc/Buttons";
import { StatusBadge } from "../../components/vc/StatusBadge";
import { ImageViewer } from "../../components/vc/ImageViewer";
import { ImageStrip } from "../../components/vc/ImageStrip";
import { ChatButton } from "../../components/vc/ChatButton";
import {
  ArrowLeft, Building2, Calendar, User, PauseCircle, CheckCircle2, ClipboardList, X, Phone, Clock, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const STATUS_MAP = {
  placed: "pending",
  in_progress: "assigned",
  delivered: "delivered",
  cancelled: "closed",
};

// Phase 7 #6 — same priority set used across Assign Task / Assign Order.
const PRIORITY_OPTIONS = [
  { v: "urgent", l: "Urgent", cls: "bg-red-100 text-red-700 ring-red-300" },
  { v: "high", l: "High", cls: "bg-amber-100 text-amber-700 ring-amber-300" },
  { v: "medium", l: "Medium", cls: "bg-sky-100 text-sky-700 ring-sky-300" },
  { v: "low", l: "Low", cls: "bg-slate-100 text-slate-600 ring-slate-300" },
];

export default function VCIncomingOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [empId, setEmpId] = useState("");
  const [priority, setPriority] = useState("medium");
  const [shareContact, setShareContact] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [savingDelivery, setSavingDelivery] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get(`/inbound-orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => { toast.error("Order not found"); navigate("/orders"); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    apiClient.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, [id]);

  // #3 — viewing this order's detail page marks its notification read
  // immediately, whether the user tapped a toast to get here or navigated
  // in directly (e.g. via the Orders list already open).
  useMarkNotificationReadForEntity(id);

  const setStatus = async (status, label, { autoAssign = false } = {}) => {
    setBusy(true);
    try {
      await apiClient.put(`/inbound-orders/${id}/status`, { status });
      toast.success(label);
      // #15 — after supplier accepts an order, auto-open the Assign Employee
      // dialog so they don't have to click again on the next screen.
      if (autoAssign) setAssignOpen(true);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doAssign = async () => {
    if (!empId) { toast.error("Pick an employee"); return; }
    setAssigning(true);
    try {
      // 1. mark order in_progress if not already
      if (order.status !== "in_progress") {
        await apiClient.put(`/inbound-orders/${id}/status`, { status: "in_progress" });
      }
      // 2. Single call: persists the supplier-side assignment on the order
      // AND (re)assigns the linked task server-side — reassigning to a new
      // employee moves the SAME task instead of leaving a stale duplicate
      // on the previous employee's list (Phase 3 #1/#2).
      const emp = employees.find((e) => e.id === empId);
      await apiClient.post(`/inbound-orders/${order.id}/assignment`, {
        employee_id: empId,
        share_contact: !!shareContact,
        priority,
      });
      toast.success(`Assigned to ${emp?.name || "employee"}`);
      setAssignOpen(false);
      setShareContact(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setAssigning(false);
    }
  };

  // Phase 7 #4 — the order acceptor (this supplier) sets/updates the
  // expected delivery date+time; visible unchanged to buyer + employee.
  const openDelivery = () => {
    setDeliveryDate(order.expected_delivery_date || "");
    setDeliveryTime(order.expected_delivery_time || "");
    setDeliveryOpen(true);
  };

  const saveDelivery = async () => {
    setSavingDelivery(true);
    try {
      const { data } = await apiClient.put(`/inbound-orders/${id}/delivery`, {
        expected_delivery_date: deliveryDate || null,
        expected_delivery_time: deliveryTime || null,
      });
      setOrder(data);
      toast.success("Delivery time saved");
      setDeliveryOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSavingDelivery(false);
    }
  };

  const [priceBusy, setPriceBusy] = useState(null); // key of line being saved, or "flat"

  // Supplier picks ONE of (quoted, negotiated) per item/line. Only possible
  // while the order is still "placed" — i.e. before it's accepted. Saves
  // immediately via its own endpoint so the buyer's total reflects the pick
  // right away, without needing a separate "confirm" step.
  const choosePrice = async (lineIndex, choice) => {
    const key = lineIndex == null ? "flat" : lineIndex;
    setPriceBusy(key);
    try {
      const { data } = await apiClient.put(`/inbound-orders/${id}/price-selection`, {
        line_index: lineIndex,
        choice,
      });
      setOrder(data);
      toast.success(choice === "negotiated" ? "Using negotiated price" : "Using quoted price");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setPriceBusy(null);
    }
  };

  const dateStr = useMemo(() => {
    if (!order?.created_at) return "";
    return new Date(order.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }, [order]);

  if (loading || !order) {
    return (
      <>
        <Header />
        <div className="px-4 py-8 text-center text-slate-500">Loading...</div>
      </>
    );
  }

  const displayStatus = STATUS_MAP[order.status] || "pending";
  const isTerminal = ["delivered", "cancelled"].includes(order.status);

  return (
    <>
      <Header />
      <div className="px-4 pt-4 pb-32 space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="inbound-detail-back" className="flex items-center gap-1 text-sm font-semibold text-slate-600">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <ChatButton orderId={order.id} testid="inbound-detail-chat-btn" />
        </div>

        {/* Business card */}
        <div data-testid="inbound-business-card" className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center shrink-0">
              <Building2 className="w-5 h-5" strokeWidth={2.2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Buyer</div>
              <div className="font-bold text-slate-900 text-base leading-tight">{order.buyer_name || "Buyer"}</div>
              <div className="text-xs text-slate-500 mt-0.5 font-mono">#{(order.id || "").slice(0, 6).toUpperCase()}</div>
            </div>
            <StatusBadge status={displayStatus} size="lg" testid="inbound-detail-status" />
          </div>
        </div>

        {/* Order table */}
        <div data-testid="inbound-order-table" className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order summary</div>
              <div className="font-semibold text-slate-900 text-sm mt-0.5 truncate">{order.product}</div>
            </div>
            {order.lines && order.lines.length > 0 && (
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 tabular-nums shrink-0 ml-2">
                {order.lines.length} item{order.lines.length === 1 ? "" : "s"}
              </div>
            )}
          </div>
          {order.lines && order.lines.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {order.lines.map((ln, i) => {
                const qty = Number(ln.quantity || 0);
                const unitPrice = Number(ln.unit_price != null ? ln.unit_price : (qty ? (Number(ln.price || 0) / qty) : 0));
                const lineTotal = Number(ln.price != null ? ln.price : unitPrice * qty);
                return (
                  <div key={i} className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="w-5 shrink-0 text-center text-[11px] font-bold text-slate-400 tabular-nums">{i + 1}.</span>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-slate-900 text-sm truncate">{ln.product}</div>
                        <div data-testid={`inbound-line-formula-${i}`} className="text-[11px] text-slate-500 tabular-nums">
                          ₹{unitPrice.toFixed(2)} × {qty} {ln.unit}
                        </div>
                      </div>
                      <div data-testid={`inbound-line-total-${i}`} className="text-sm font-bold text-slate-900 shrink-0 tabular-nums">₹{lineTotal.toFixed(2)}</div>
                    </div>
                    {ln.negotiated_price != null && (
                      <PricePicker
                        quoted={ln.quoted_price ?? ln.price}
                        negotiated={ln.negotiated_price}
                        choice={ln.price_choice || "quoted"}
                        editable={order.status === "placed"}
                        busy={priceBusy === i}
                        onChoose={(c) => choosePrice(i, c)}
                        testid={`inbound-line-price-picker-${i}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 font-semibold truncate">{order.quantity} {order.unit}</div>
                  {order.unit_price ? (
                    <div className="text-[11px] text-slate-500 tabular-nums">
                      ₹{Number(order.unit_price).toFixed(2)} × {order.quantity} {order.unit}
                    </div>
                  ) : null}
                </div>
                <div className="text-sm font-bold text-slate-900 shrink-0 tabular-nums">₹{Number(order.price || 0).toFixed(2)}</div>
              </div>
              {order.negotiated_price != null && (
                <PricePicker
                  quoted={order.quoted_price ?? order.price}
                  negotiated={order.negotiated_price}
                  choice={order.price_choice || "quoted"}
                  editable={order.status === "placed"}
                  busy={priceBusy === "flat"}
                  onChoose={(c) => choosePrice(null, c)}
                  testid="inbound-flat-price-picker"
                />
              )}
            </div>
          )}
          <div className="px-4 py-2.5 bg-emerald-50 flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Total</div>
            <div className="text-lg font-bold text-emerald-900 tabular-nums">₹{Number(order.price || 0).toFixed(2)}</div>
          </div>
        </div>

        {/* Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-4 space-y-2.5 shadow-sm">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Order information</div>
          <InfoRow Icon={Calendar} label="Placed on" value={dateStr} />
          <InfoRow Icon={User} label="Requested by" value={order.buyer_name || "Buyer"} />
          {order.notes && <InfoRow Icon={ClipboardList} label="Notes" value={order.notes} />}
          {/* Assigned employee (supplier-side control). Name is always shown
              once the supplier assigns someone; phone/call icon appears only
              when the supplier ticks "Share contact with customer". */}
          {order.assigned_employee_name && (
            <div className="pt-2 border-t border-slate-100" data-testid="order-assigned-employee">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Assigned to</div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-900">{order.assigned_employee_name}</span>
                {order.share_employee_contact && order.assigned_employee_phone && (
                  <a
                    href={`tel:${order.assigned_employee_phone}`}
                    data-testid="order-assigned-employee-call"
                    className="w-10 h-10 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 grid place-items-center"
                    aria-label="Call employee"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {order.images && order.images.length > 0 ? (
          <div data-testid="incoming-order-images-block" className="bg-white rounded-2xl border border-slate-100 p-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-1 pb-2">
              {order.images.length} attached {order.images.length === 1 ? "image" : "images"}
            </div>
            <ImageStrip images={order.images} testid="incoming-order-image-strip" />
          </div>
        ) : (order.image_data && (
          <ImageViewer src={order.image_data} label="Order image" testid="incoming-order-image" />
        ))}

        {!isTerminal && order.status !== "placed" && (
          <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between shadow-sm" data-testid="delivery-time-card">
            <div className="flex items-start gap-2.5 min-w-0">
              <Clock className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" strokeWidth={2.2} />
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Expected delivery</div>
                {order.expected_delivery_date || order.expected_delivery_time ? (
                  <div className="text-sm text-slate-900 mt-0.5 font-semibold" data-testid="delivery-time-value">
                    {[order.expected_delivery_date, order.expected_delivery_time].filter(Boolean).join(" · ")}
                  </div>
                ) : (
                  <div className="text-sm text-slate-400 mt-0.5">Not set yet</div>
                )}
              </div>
            </div>
            <button
              onClick={openDelivery}
              data-testid="edit-delivery-btn"
              className="h-9 px-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold shrink-0 flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> {order.expected_delivery_date || order.expected_delivery_time ? "Edit" : "Set"}
            </button>
          </div>
        )}

        {/* Action buttons */}
        {!isTerminal && (
          <div className="space-y-2 pt-2">
            {order.status === "placed" && (
              <>
                <PrimaryButton
                  onClick={() => setStatus("in_progress", "Order accepted — assign to your team", { autoAssign: true })}
                  loading={busy}
                  data-testid="inbound-accept-btn"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> Accept Order
                </PrimaryButton>
                <SecondaryButton
                  onClick={() => setStatus("cancelled", "Order rejected")}
                  data-testid="inbound-reject-btn"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <PauseCircle className="w-4 h-4" /> Keep On Hold / Reject
                </SecondaryButton>
              </>
            )}
            {order.status === "in_progress" && (
              <>
                <button
                  onClick={() => setAssignOpen(true)}
                  data-testid="inbound-assign-employee-btn"
                  className="w-full h-14 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-sm shadow-indigo-500/30 transition active:scale-[0.98]"
                >
                  <ClipboardList className="w-4 h-4" />
                  {order.assigned_employee_name ? "Reassign to another employee" : "Assign To Employee"}
                </button>
                <PrimaryButton
                  onClick={() => setStatus("delivered", "Marked delivered")}
                  data-testid="inbound-deliver-btn"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" /> Mark Delivered
                </PrimaryButton>
              </>
            )}
          </div>
        )}
      </div>

      {/* Assign employee sheet */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle>{order.assigned_employee_name ? "Reassign to Another Employee" : "Assign to Employee"}</DialogTitle>
            <DialogDescription>
              {order.assigned_employee_name
                ? `Currently assigned to ${order.assigned_employee_name}. Pick a new team member to take over.`
                : "Pick a team member and they will get this in their tasks."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {employees.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6">
                No employees yet.{" "}
                <button onClick={() => navigate("/employees")} className="text-emerald-700 font-semibold underline">Add one</button>
              </div>
            ) : (
              <>
                <Select value={empId || "none"} onValueChange={(v) => setEmpId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="assign-emp-select" className="h-12 rounded-2xl bg-white">
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Choose employee —</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}{e.role ? ` · ${e.role}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Priority</div>
                  <div className="grid grid-cols-4 gap-1.5">
                    {PRIORITY_OPTIONS.map((p) => (
                      <button
                        key={p.v}
                        type="button"
                        onClick={() => setPriority(p.v)}
                        data-testid={`assign-priority-${p.v}`}
                        className={`h-9 rounded-xl text-[11px] font-bold transition ring-2 ${priority === p.v ? p.cls + " ring-offset-1" : "bg-white text-slate-400 ring-slate-100"}`}
                      >
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Supplier controls whether their employee's contact is visible
                    to the customer on this specific order. Off by default. */}
                <label
                  className="flex items-start gap-2 pt-1 cursor-pointer"
                  data-testid="assign-share-contact-label"
                >
                  <input
                    type="checkbox"
                    checked={shareContact}
                    onChange={(e) => setShareContact(e.target.checked)}
                    data-testid="assign-share-contact"
                    className="w-4 h-4 mt-0.5 rounded accent-emerald-600"
                  />
                  <span className="text-sm text-slate-700 leading-snug">
                    Share assigned employee contact with customer
                    <span className="block text-[11px] text-slate-500">
                      When enabled, the customer sees the employee&apos;s name
                      and phone on their Order Details. Off keeps only the name
                      visible.
                    </span>
                  </span>
                </label>

                <div className="grid grid-cols-2 gap-2">
                  <SecondaryButton onClick={() => setAssignOpen(false)} data-testid="assign-cancel" className="flex items-center justify-center gap-1">
                    <X className="w-4 h-4" /> Cancel
                  </SecondaryButton>
                  <PrimaryButton onClick={doAssign} loading={assigning} data-testid="assign-confirm">
                    Assign
                  </PrimaryButton>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delivery time — Phase 7 #4 */}
      <Dialog open={deliveryOpen} onOpenChange={setDeliveryOpen}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="delivery-dialog">
          <DialogHeader>
            <DialogTitle>Expected Delivery</DialogTitle>
            <DialogDescription>Visible to the customer, you and any employee assigned to this order.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Delivery date</Label>
              <Input type="date" data-testid="delivery-date-input" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="h-12 rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Delivery time</Label>
              <Input type="time" data-testid="delivery-time-input" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} className="h-12 rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <SecondaryButton onClick={() => setDeliveryOpen(false)} data-testid="delivery-cancel">Cancel</SecondaryButton>
              <PrimaryButton onClick={saveDelivery} loading={savingDelivery} data-testid="delivery-save">Save</PrimaryButton>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Buyer proposed a different price while ordering from a quotation. The
// quote itself never changes — the supplier just picks which one applies.
function PricePicker({ quoted, negotiated, choice, editable, busy, onChoose, testid }) {
  const opts = [
    { v: "quoted", l: "Quoted price", amt: quoted },
    { v: "negotiated", l: "Buyer's offer", amt: negotiated },
  ];
  return (
    <div data-testid={testid} className="mt-2 pl-7 flex flex-wrap gap-1.5">
      {opts.map((o) => {
        const active = choice === o.v;
        return (
          <button
            key={o.v}
            type="button"
            disabled={!editable || busy}
            onClick={() => editable && onChoose(o.v)}
            data-testid={`${testid}-${o.v}`}
            data-selected={active ? "true" : "false"}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition ${
              active
                ? "bg-emerald-600 border-emerald-600 text-white"
                : "bg-white border-slate-200 text-slate-600"
            } ${editable ? "cursor-pointer active:scale-[0.97]" : "opacity-70"}`}
          >
            {o.l}: ₹{Number(o.amt || 0).toFixed(2)}
          </button>
        );
      })}
      {!editable && (
        <span className="text-[10px] text-slate-400 self-center">Locked after acceptance</span>
      )}
    </div>
  );
}

function InfoRow({ Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" strokeWidth={2.2} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
        <div className="text-sm text-slate-900 mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}
